import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';

const Login = ({ onLoginSuccess }) => {
  const { theme } = useTheme();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check for error in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    
    if (errorParam === 'domain_restricted') {
      setError('Access denied. Only @highperformr.ai or @gethighperformr.com email addresses are allowed to access this application.');
    } else if (errorParam === 'auth_failed') {
      setError('Authentication failed. Please try again.');
    }
    
    // Clean up URL parameters after showing error
    if (errorParam) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  const handleGoogleLogin = () => {
    setLoading(true);
    setError('');
    // Redirect to Google OAuth
    const apiBase = process.env.NODE_ENV === 'production' 
      ? 'https://admin.highperformr.ai' 
      : 'http://localhost:4000';
    window.location.href = `${apiBase}/auth/google`;
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: theme.background,
      fontFamily: 'Arial, sans-serif',
      transition: 'background-color 0.3s ease'
    }}>
      <div style={{
        backgroundColor: theme.cardBackground,
        padding: '40px',
        borderRadius: '10px',
        boxShadow: `0 4px 6px ${theme.cardShadow}`,
        textAlign: 'center',
        maxWidth: '400px',
        width: '100%',
        border: `1px solid ${theme.border}`,
        transition: 'background-color 0.3s ease, border-color 0.3s ease'
      }}>
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ 
            color: theme.text, 
            marginBottom: '10px',
            fontSize: '28px',
            fontWeight: 'bold'
          }}>
            HP Admin
          </h1>
        </div>

        {error && (
          <div style={{
            backgroundColor: theme.name === 'dark' ? '#5c2a2a' : '#f8d7da',
            color: theme.name === 'dark' ? '#ffaaaa' : '#721c24',
            padding: '15px',
            borderRadius: '5px',
            marginBottom: '20px',
            fontSize: '14px',
            border: error.includes('Access denied') 
              ? `2px solid ${theme.danger}` 
              : `1px solid ${theme.name === 'dark' ? '#7c3a3a' : '#f5c6cb'}`,
            textAlign: 'left'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              marginBottom: error.includes('Access denied') ? '10px' : '0'
            }}>
              <span style={{ fontSize: '18px' }}>
                {error.includes('Access denied') ? '🚫' : '⚠️'}
              </span>
              <strong style={{ fontSize: '16px' }}>
                {error.includes('Access denied') ? 'Access Denied' : 'Authentication Error'}
              </strong>
            </div>
            <div style={{ marginLeft: '28px' }}>
              {error}
            </div>
          </div>
        )}

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ 
            color: theme.text, 
            marginBottom: '10px',
            fontSize: '20px'
          }}>
            Sign In Required
          </h2>
          <p style={{ 
            color: theme.textSecondary, 
            margin: 0,
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            Please sign in with your @highperformr.ai or @gethighperformr.com Google account to access the dashboard.
          </p>
        </div>

        <button
          onClick={handleGoogleLogin}
          disabled={loading}
          style={{
            width: '100%',
            padding: '12px 20px',
            backgroundColor: loading ? '#6c757d' : '#4285f4',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            transition: 'background-color 0.3s ease'
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = '#3367d6';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = '#4285f4';
            }
          }}
        >
          {loading ? (
            <>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid #ffffff',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              Signing in...
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </>
          )}
        </button>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Login;
